import { isFuture, parseISO, parse, isPast, differenceInDays } from 'core/utils/date'

const CERTIFICATE_OF_INSURANCE_DOCUMENT_TYPE = 'certificate_of_insurance'

export const MAX_UPLOADED_FILES_PER_SHIPPER = 5

export type AttachmentDocumentType = 'certificate_of_insurance' | 'w9' | 'other' | 'client_contract'

export type Document = {
  file?: File
  type?: AttachmentDocumentType
  expirationDate?: string // Date in the format "yyyy-MM-dd" (e.g. 2026-05-31).
}

export type AttachmentDocumentTypeOption = {
  value: string
  label: string
}

export type ShipperAttachment = {
  id: number
  fileName: string
  documentType: AttachmentDocumentType
  expirationDate?: string
  uploadedBy: 'carrier' | 'shipper'
}

export type ShipperAttachmentDetail = {
  fileUrl: string
}

export function isCertificateOfInsurance(document: Document) {
  return document.type === CERTIFICATE_OF_INSURANCE_DOCUMENT_TYPE
}

function isExpirationDateValid(expirationDate: string | undefined): boolean {
  if (!expirationDate) return false
  return isFuture(parseISO(expirationDate))
}

export function isDocumentValid(document: Document): boolean {
  const isCOI = isCertificateOfInsurance(document)
  const hasValidExpirationDate = isExpirationDateValid(document.expirationDate)
  if (!document.type) return false
  if (!document.file) return false
  if (isCOI && !hasValidExpirationDate) return false
  return true
}

export const COI_EXPIRING_SOON_THRESHOLD_DAYS = 30

export type CertificateExpirationStatus = 'valid' | 'expiring_soon' | 'expired'

export function getCertificateExpirationStatus(
  attachment: Pick<ShipperAttachment, 'documentType' | 'expirationDate'>
): CertificateExpirationStatus | null {
  const { documentType, expirationDate } = attachment

  if (documentType !== 'certificate_of_insurance' || !expirationDate) {
    return null
  }

  const expiration = parse(expirationDate, 'yyyy-MM-dd', new Date())
  const today = new Date()

  if (isPast(expiration)) {
    return 'expired'
  }

  if (differenceInDays(expiration, today) <= COI_EXPIRING_SOON_THRESHOLD_DAYS) {
    return 'expiring_soon'
  }

  return 'valid'
}
